import React from 'react';
import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import ChatPage from '../pages/ChatPage';
import CustomerProfile from '../pages/CustomerProfile';
import CustomerNotifications from '../pages/CustomerNotifications';
import { useAuth } from '../context/AuthContext';

// Public components
import DigitalMarketplaceLanding from '../components/DigitalMarketplaceLanding';
import LandingPage from '../components/LandingPage';
import LoginPage from '../components/LoginPage';
import UnifiedAuthPage from '../components/UnifiedAuthPage';
import OAuthCompletionPage from '../components/OAuthCompletionPage';
import OAuthCallbackPage from '../components/OAuthCallbackPage';
import ForgotPassword from '../components/ForgotPassword';
import ResetPassword from '../components/ResetPassword';
import HowItWorksPage from '../components/HowItWorksPage';
import FeaturesPage from '../components/FeaturesPage';
import AboutPage from '../components/AboutPage';
import ServicesPage from '../components/ServicesPage';
import APIPage from '../components/APIPage';
import IntegrationsPage from '../components/IntegrationsPage';
import HelpCenterPage from '../components/HelpCenterPage';
import ContactPage from '../components/ContactPage';
import CareersPage from '../components/CareersPage';
import StatusPage from '../components/StatusPage';
import PolicyPage from '../components/PolicyPage';
import SMSTermsPage from '../pages/SMSTermsPage';
import TailorPublicPage from '../pages/TailorPublicPage';
import CustomerLinking from '../pages/CustomerLinking';
import CustomerInvitation from '../pages/CustomerInvitation';
import PaymentSuccess from '../pages/PaymentSuccess';
import PaymentCancelled from '../pages/PaymentCancelled';
import PaymentRazorpay from '../pages/PaymentRazorpay';
import PricingPage from '../pages/PricingPage';
import PaymentMethodsPage from '../pages/PaymentMethodsPage';
import BillingHistoryPage from '../pages/BillingHistoryPage';

// Customer components
import { CustomerDashboard, CustomerOrderManagement } from '../components/customer';
import OrderTimeline from '../components/customer/OrderTimeline';
import MeasurementsPageSimple from '../components/customer/MeasurementsPageSimple';
import MeasurementDetails from '../components/customer/MeasurementDetails';
import NewOrderForm from '../components/customer/NewOrderForm';

// Business components  
import { BusinessDashboard } from '../components/business';
import BusinessProfile from '../components/business/BusinessProfile';
import BusinessOrderForm from '../components/business/BusinessOrderForm';
import BusinessAnalytics from '../components/business/BusinessAnalytics';

// Admin components
import AdminDashboard from '../components/AdminDashboard';

// Other components
import UploadFlow from '../components/UploadFlow';
import ResultsPage from '../components/ResultsPage';
import ShopPage from '../components/ShopPage';
import TryOnPage from '../components/TryOnPage';
import CheckoutPage from '../components/CheckoutPage';
import AIFeatures from '../components/AIFeatures';
import FashionHousesPage from '../components/FashionHousesPage';
import MeasurementBook from '../components/MeasurementBook';
import SearchHistory from '../components/SearchHistory';
import PrintExport from '../components/PrintExport';
import SettingsLanguage from '../components/SettingsLanguage';
import NotificationsPage from '../components/NotificationsPage';
import PaymentIntegration from '../components/PaymentIntegration';
import CustomerDetails from '../components/CustomerDetails';
import OrderDetails from '../components/OrderDetails';
import OrderDetail from '../components/OrderDetail';
import OrderDetailsV2 from '../components/OrderDetailsV2/OrderDetailsV2';

// Layout components
import CustomerLayout from './CustomerLayout';
import BusinessLayout from './BusinessLayout';
import AdminLayout from './AdminLayout';

// Protected route component
import ProtectedRoute from '../components/ProtectedRoute';

// Redirect component for legacy tailor routes
const TailorCustomerRedirect: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/business/customers/${id}`} replace />;
};

const AppRoutes: React.FC = () => {
  const { user } = useAuth();

  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/home" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<UnifiedAuthPage />} />
      <Route path="/auth" element={<UnifiedAuthPage />} />
      <Route path="/auth/oauth-callback" element={<OAuthCallbackPage />} />
      <Route path="/auth/complete-oauth" element={
        <ProtectedRoute allowPendingRole={true}>
          <OAuthCompletionPage />
        </ProtectedRoute>
      } />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/how-it-works" element={<HowItWorksPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      <Route path="/features" element={<FeaturesPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/services" element={<ServicesPage />} />
      <Route path="/api" element={<APIPage />} />
      <Route path="/integrations" element={<IntegrationsPage />} />
      <Route path="/help-center" element={<HelpCenterPage />} />
      <Route path="/contact" element={<ContactPage />} />
      <Route path="/status" element={<StatusPage />} />
      <Route path="/t/:slug" element={<TailorPublicPage />} />
      <Route path="/blog" element={<AboutPage />} />
      <Route path="/careers" element={<CareersPage />} />
      <Route path="/faqs" element={<HelpCenterPage />} />
      <Route path="/privacy-policy" element={<PolicyPage type="privacy" />} />
      <Route path="/terms-of-service" element={<PolicyPage type="terms" />} />
      <Route path="/terms-and-conditions" element={<PolicyPage type="terms" />} />
      <Route path="/cookie-policy" element={<PolicyPage type="cookies" />} />
      <Route path="/sms-terms" element={<SMSTermsPage />} />
      
      {/* Customer Linking Route - Public */}
      <Route path="/customer/link/:linkToken" element={<CustomerLinking />} />
      
      {/* Customer Invitation Route - Public */}
      <Route path="/customer/invite/:token" element={<CustomerInvitation />} />
      
      {/* Payment Routes - Public */}
      <Route path="/payment-success" element={<PaymentSuccess />} />
      <Route path="/payment-cancelled" element={<PaymentCancelled />} />
      <Route path="/payment-razorpay" element={<PaymentRazorpay />} />
      
      {/* Customer Routes */}
      <Route path="/customer/*" element={
        <ProtectedRoute requiredRole="customer">
          <CustomerLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<CustomerDashboard />} />
        <Route path="orders" element={<CustomerOrderManagement />} />
        <Route path="orders/new" element={<NewOrderForm onClose={() => window.history.back()} onSubmit={() => {}} />} />
        <Route path="orders/:id/timeline" element={<OrderTimeline />} />
        <Route path="measurements" element={<MeasurementsPageSimple />} />
        <Route path="measurements/new" element={<div>New Measurement</div>} />
        <Route path="measurements/:id" element={<MeasurementDetails />} />
        <Route path="profile" element={<CustomerProfile />} />
        <Route path="notifications" element={<CustomerNotifications />} />
        <Route path="chat" element={<ChatPage />} />
        <Route path="chat/:tailorId/:tailorName" element={<ChatPage />} />
        <Route path="payment-methods" element={<PaymentMethodsPage />} />
        <Route path="billing" element={<BillingHistoryPage />} />
        <Route path="*" element={<Navigate to="/customer/dashboard" replace />} />
      </Route>
      
      {/* Business Routes */}
      <Route path="/business/*" element={
        <ProtectedRoute requiredRole="tailor">
          <BusinessLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<BusinessDashboard />} />
        <Route path="orders" element={<div>Business Order Management</div>} />
        <Route path="orders/new" element={<BusinessOrderForm onClose={() => window.history.back()} onSubmit={() => {}} />} />
        <Route path="customers" element={<div>Customer Directory</div>} />
        <Route path="customers/:id" element={<CustomerDetails />} />
        <Route path="analytics" element={<BusinessAnalytics />} />
        <Route path="profile" element={<BusinessProfile />} />
        <Route path="payment-methods" element={<PaymentMethodsPage />} />
        <Route path="billing" element={<BillingHistoryPage />} />
        <Route path="*" element={<Navigate to="/business/dashboard" replace />} />
      </Route>
      
      {/* Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute requiredRole="admin">
          <AdminLayout />
        </ProtectedRoute>
      }>
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="users" element={<div>User Management</div>} />
        <Route path="analytics" element={<div>Admin Analytics</div>} />
        <Route path="settings" element={<div>Admin Settings</div>} />
        <Route path="*" element={<Navigate to="/admin/dashboard" replace />} />
      </Route>
      
      {/* Legacy Routes - Redirect to new structure */}
      <Route path="/user/dashboard" element={<Navigate to="/customer/dashboard" replace />} />
      <Route path="/user/orders" element={<Navigate to="/customer/orders" replace />} />
      <Route path="/user/measurements" element={<Navigate to="/customer/measurements" replace />} />
      <Route path="/user/profile" element={<Navigate to="/customer/profile" replace />} />
      
      <Route path="/tailor/dashboard" element={<Navigate to="/business/dashboard" replace />} />
      <Route path="/tailor/orders" element={<Navigate to="/business/orders" replace />} />
      <Route path="/tailor/customers" element={<Navigate to="/business/customers" replace />} />
      <Route path="/tailor/customers/:id" element={<TailorCustomerRedirect />} />
      <Route path="/tailor/analytics" element={<Navigate to="/business/analytics" replace />} />
      <Route path="/tailor/profile" element={<Navigate to="/business/profile" replace />} />
      
      {/* Other Routes */}
      <Route path="/upload" element={<UploadFlow onComplete={() => {}} />} />
      <Route path="/results" element={<ResultsPage />} />
      <Route path="/shop" element={<ShopPage />} />
      <Route path="/try-on" element={<TryOnPage />} />
      <Route path="/checkout" element={<CheckoutPage />} />
      <Route path="/ai-features" element={<AIFeatures />} />
      <Route path="/fashion-houses" element={<FashionHousesPage />} />
      <Route path="/measurement-book" element={<MeasurementBook />} />
      <Route path="/search-history" element={<SearchHistory />} />
      <Route path="/print-export" element={<PrintExport />} />
      <Route path="/settings" element={<SettingsLanguage />} />
      <Route path="/notifications" element={<NotificationsPage />} />
      <Route path="/payment" element={<PaymentIntegration />} />
      <Route path="/customer-details" element={<CustomerDetails />} />
      <Route path="/order-details" element={<OrderDetails />} />
      {/* New Modern Order Details Route */}
      <Route path="/order/:orderNumber/details" element={
        <ProtectedRoute requiredRole={["tailor", "customer"]}>
          <OrderDetailsV2 />
        </ProtectedRoute>
      } />
      
      {/* Legacy Order Timeline Route - Keep for backward compatibility, will be removed */}
      <Route path="/order/:orderNumber/timeline" element={
        <ProtectedRoute requiredRole={["tailor", "customer"]}>
          <OrderDetail />
        </ProtectedRoute>
      } />
      
      {/* Chat Route */}
      <Route path="/chat" element={
        <ProtectedRoute>
          <ChatPage />
        </ProtectedRoute>
      } />
      
      {/* Fallback */}
      <Route path="*" element={<DigitalMarketplaceLanding />} />
    </Routes>
  );
};

export default AppRoutes;
